/**
 * Shared connection-reference binding helpers for create_flow / update_flow.
 *
 * Two gaps lived in the flow write paths:
 *
 *  1. update_flow never declared the `$connections` workflow parameter when an
 *     update introduced the flow's first connector action, so the service
 *     rejected the save with InvalidTemplate ("The workflow parameters
 *     '$connections' are not valid; they are not declared in the definition").
 *     create_flow declared it for new flows; the update path only carried over
 *     whatever parameters the current definition already had.
 *
 *  2. A connectionReference with source "Invoker" carries a LOGICAL
 *     connectionName (e.g. "shared_sharepointonline"), not a concrete
 *     connection id. Passing that name straight to the flows API fails with
 *     ConnectionAuthorizationFailed, which the error mappers reported as
 *     "Missing connection. Create it at make.powerautomate.com..." even when a
 *     perfectly good connection existed in the environment. The logical name
 *     must be resolved to a real connection before the save, and "missing"
 *     should only be reported when no connection of that connector type exists
 *     at all — naming the environment that was checked.
 */

import type { Connection, ConnectionReference, FlowDefinition } from "../api/types.js";
import { extractRequiredConnections } from "../validator/connection-validator.js";

/** The subset of the API surface these helpers need. Kept minimal so partial
 * test stubs (and preview paths) that lack listConnections degrade gracefully
 * instead of throwing. */
export interface ConnectionListingApi {
  listConnections?(environmentId?: string): Promise<Connection[]>;
  getDefaultEnvironmentId?(): string;
}

/**
 * Ensure the workflow declares the `$connections` / `$authentication`
 * parameters whenever connections are in play — either connection references
 * are attached, or the definition contains OpenApiConnection actions/triggers.
 *
 * Idempotent, and never clobbers parameters the definition already declares.
 * The parameters object is rebuilt (not mutated in place) so a definition that
 * was shallow-copied from a fetched flow never mutates the original.
 */
export function ensureConnectionParameters(
  definition: FlowDefinition,
  connectionReferences: Record<string, ConnectionReference> | undefined,
): void {
  const hasRefs =
    !!connectionReferences && Object.keys(connectionReferences).length > 0;
  const usesConnectors = extractRequiredConnections(definition).size > 0;
  if (!hasRefs && !usesConnectors) return;

  definition.parameters = {
    ...definition.parameters,
    $connections: definition.parameters?.$connections ?? {
      defaultValue: {},
      type: "Object",
    },
    $authentication: definition.parameters?.$authentication ?? {
      defaultValue: {},
      type: "SecureObject",
    },
  };
}

export interface ConnectionResolution {
  /** The (possibly rewritten) connection references to send to the API. */
  refs: Record<string, ConnectionReference>;
  /** Human-readable notes about logical names that were resolved. */
  resolved: string[];
  /** Blocking problems: a logical reference names a connector with NO
   * connection in the checked environment. Saving would fail anyway — with a
   * misleading error — so callers should surface these instead. */
  missing: string[];
}

/** Trailing path segment of a connector id, e.g.
 * "/providers/Microsoft.PowerApps/apis/shared_sharepointonline" →
 * "shared_sharepointonline". */
function connectorApiName(ref: ConnectionReference): string | undefined {
  const fromId = ref.id?.split("/").filter(Boolean).pop();
  if (fromId && fromId !== "apis") return fromId;
  if (ref.connectionName?.startsWith("shared_")) return ref.connectionName;
  return undefined;
}

/** True when a connectionName is a connector's logical name rather than a
 * concrete connection id (real ids are opaque, e.g.
 * "6d1ea6a92b99432b81212b02d3e09373"). */
function isLogicalConnectionName(
  name: string | undefined,
  apiName: string | undefined,
): boolean {
  if (!name) return false;
  if (name.startsWith("shared_")) return true;
  if (apiName && (name === apiName || name === apiName.replace(/^shared_/, ""))) {
    return true;
  }
  return false;
}

function connectionStatus(conn: Connection): string {
  return conn.properties.statuses?.[0]?.status ?? "Unknown";
}

/** One-line inventory of a connector's connections, for error messages. */
export function describeConnectorConnections(connections: Connection[]): string {
  if (connections.length === 0) return "none";
  return connections
    .map(
      (c) =>
        `'${c.name}' (${c.properties.displayName ?? "unnamed"}, ${connectionStatus(c)})`,
    )
    .join(", ");
}

/**
 * Resolve logical connection names to real connections in the target
 * environment.
 *
 * A reference needs resolution when its source is "Invoker" (its
 * connectionName is a logical key by definition) or when its connectionName is
 * connector-shaped (starts with "shared_" / equals the connector api name).
 * References whose connectionName already matches a real connection are left
 * untouched. When the connector has no connection at all, that is reported as
 * a blocking `missing` entry naming the environment — the save would fail
 * anyway, with a message telling the user to create a connection that may
 * simply live in a different environment.
 *
 * Fail-open: if connections cannot be listed (stubbed API, transient error),
 * the references are returned unchanged and the API remains the arbiter.
 */
export async function resolveLogicalConnectionNames(
  api: ConnectionListingApi,
  refs: Record<string, ConnectionReference>,
  environmentId: string | undefined,
  environmentLabel: string,
): Promise<ConnectionResolution> {
  const result: ConnectionResolution = { refs, resolved: [], missing: [] };

  const candidates = Object.entries(refs).filter(([, ref]) => {
    return (
      ref.source === "Invoker" ||
      isLogicalConnectionName(ref.connectionName, connectorApiName(ref))
    );
  });
  if (candidates.length === 0) return result;

  let connections: Connection[];
  try {
    if (typeof api.listConnections !== "function") return result;
    connections = await api.listConnections(environmentId);
  } catch {
    return result;
  }

  const byName = new Map(connections.map((c) => [c.name, c]));
  const byConnector = new Map<string, Connection[]>();
  for (const conn of connections) {
    const apiName = conn.properties.apiId?.split("/").filter(Boolean).pop();
    if (!apiName) continue;
    const list = byConnector.get(apiName) ?? [];
    list.push(conn);
    byConnector.set(apiName, list);
  }

  const rewritten: Record<string, ConnectionReference> = { ...refs };
  for (const [key, ref] of candidates) {
    // Already a real connection (an Invoker ref may legitimately carry one).
    if (ref.connectionName && byName.has(ref.connectionName)) continue;

    const connector = connectorApiName(ref);
    if (!connector) continue;

    const matches = byConnector.get(connector) ?? [];
    const pick =
      matches.find((c) => connectionStatus(c) === "Connected") ?? matches[0];

    if (pick) {
      rewritten[key] = { ...ref, connectionName: pick.name };
      result.resolved.push(
        `'${key}': resolved logical name '${ref.connectionName}' to existing ${connector} connection '${pick.name}' (${pick.properties.displayName ?? "unnamed"}, ${connectionStatus(pick)})`,
      );
    } else {
      result.missing.push(
        `No ${connector} connection exists in environment '${environmentLabel}'. ` +
          `Create one at make.powerautomate.com > Data > Connections (in that environment), ` +
          `or pass the environment where the connection lives.`,
      );
    }
  }

  result.refs = rewritten;
  return result;
}

/** Resolve a human-readable label for the environment a flow write targets. */
export function environmentLabelFor(
  api: ConnectionListingApi,
  environmentId: string | undefined,
): string {
  if (environmentId) return environmentId;
  try {
    const fallback = api.getDefaultEnvironmentId?.();
    if (fallback) return `${fallback} (default)`;
  } catch {
    // fall through
  }
  return "the default environment";
}

/**
 * Enrich a ConnectionAuthorizationFailed error with what actually exists in
 * the checked environment. Returns undefined when nothing could be listed —
 * callers should then fall back to their existing generic message.
 */
export async function describeConnectionAuthFailure(
  api: ConnectionListingApi,
  rawMessage: string,
  environmentId: string | undefined,
  environmentLabel: string,
): Promise<string | undefined> {
  const connMatch = rawMessage.match(/apis\/([^/'"]+)/);
  const connector = connMatch?.[1];
  if (!connector) return undefined;

  let connections: Connection[];
  try {
    if (typeof api.listConnections !== "function") return undefined;
    connections = await api.listConnections(environmentId);
  } catch {
    return undefined;
  }

  const forConnector = connections.filter(
    (c) => c.properties.apiId?.split("/").filter(Boolean).pop() === connector,
  );
  const inventory = describeConnectorConnections(forConnector);
  const friendly = connector.replace(/^shared_/, "");

  if (forConnector.length === 0) {
    return (
      `Missing '${connector}' connection in environment '${environmentLabel}'. ` +
      `Connections found for this connector there: none. ` +
      `Create one at make.powerautomate.com > Data > Connections — or, if it exists ` +
      `in a different environment, pass that environment explicitly.`
    );
  }

  return (
    `The service rejected the ${friendly} connection binding in environment ` +
    `'${environmentLabel}'. Connections that exist for this connector there: ${inventory}. ` +
    `Pass one of these as connectionName (source "Embedded"), or repair it at ` +
    `make.powerautomate.com > Data > Connections.`
  );
}
